import React, { useEffect, useState } from "react";
import SuperadminGate from "@/components/SuperadminGate";
import { useNavigate, Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import BlueprintBackground from "./BlueprintBackground";
import PageShell from "./PageShell";
import { useAuth } from "@/context/AuthContext";
import { loadBranding, peekBranding } from "@/lib/branding";

// Ported from Steeltooth's LoginPage.jsx — layout, classes, z-index stack
// and testids are verbatim (kit §6 invariants). Contained adaptations for
// Elite's auth system (kit non-negotiable: "use my app's existing auth"):
//  - Google button fires Elite's Emergent-managed Google redirect (same as
//    the Elite login page) instead of @react-oauth/google.
//  - The password field submits to Elite's portal-password login
//    (/api/auth/dev-login). Per the owner's choice, the email field is
//    hidden on this portal.

const GoogleMark = () => (
  <svg className="w-4 h-4 mr-2 shrink-0" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

export default function LoginPageSteeltooth() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  const [processingGoogle, setProcessingGoogle] = useState(false);
  // "loading" -> solid dark backdrop only; "ok" -> hero photo; "none" -> blueprint grid.
  // The grid must never flash before the photo on brands that have one.
  const [hero, setHero] = useState("loading");
  // Active brand identity — this page is shared by every brand that picks
  // the blueprint layout (Steeltooth, or a custom company). loadBranding is
  // already resolved by the login gate, so this initializes instantly.
  const [brandName, setBrandName] = useState(() => peekBranding()?.app_name || "");

  useEffect(() => {
    loadBranding().then((b) => setBrandName(b?.app_name || ""));
  }, []);

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  const handleGoogleLogin = () => {
    setProcessingGoogle(true);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT
    // URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
      redirectUrl
    )}`;
  };

  return (
    <PageShell testid="login-page">
      {hero === "none" && <BlueprintBackground />}
      <SuperadminGate />
      <div className="login-hero-photo" aria-hidden="true" data-hero-state={hero}>
        {/* Active brand's sign-in photo, served dynamically. Brands without
            a photo (or a 404) fall back to the blueprint background alone. */}
        <img
          src="/api/branding/hero"
          alt=""
          className="h-full w-full object-cover transition-opacity duration-300"
          style={{ opacity: hero === "ok" ? 1 : 0 }}
          draggable="false"
          data-testid="login-hero-img"
          onLoad={() => setHero("ok")}
          onError={(e) => { e.currentTarget.style.display = "none"; setHero("none"); }}
        />
      </div>

      <div className="relative z-10 flex min-h-[100dvh] flex-col items-center justify-center px-4 py-8">
        <div className="w-full max-w-[420px] mx-auto">
          <div className="glass rounded-xl p-5 sm:p-7" data-testid="login-panel">
            <div className="mb-5 text-center">
              <div className="flex justify-center mb-3">
                <img
                  src="/api/branding/brand-logo"
                  alt={brandName || "Company"}
                  style={{ height: 88 }}
                  className="w-auto object-contain"
                  draggable="false"
                  data-testid="brand-logo-custom"
                  onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
                />
              </div>
              <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
                Sign in{brandName ? ` to ${brandName}` : ""}
              </h1>
              <p className="text-xs text-slate-400 mt-1">
                Run the whole operation — crews, iron, jobs and billing — from one place.
              </p>
            </div>

            {processingGoogle ? (
              <div className="flex items-center justify-center py-10 text-slate-300">
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Finalizing Google sign-in…
              </div>
            ) : (
              <div className="space-y-3.5" data-testid="login-google-only">
                <button
                  type="button"
                  onClick={handleGoogleLogin}
                  data-testid="btn-login-google"
                  className="w-full h-11 rounded-md bg-[#131314] border border-[#2A3655] text-white text-sm font-semibold inline-flex items-center justify-center hover:bg-[#1c1c1e] transition-colors"
                >
                  <GoogleMark />
                  Continue with Google
                </button>
              </div>
            )}
          </div>

          <p className="mt-6 text-center text-[11px] font-semibold uppercase tracking-[0.2em] text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]" data-testid="login-tagline">
            Crews · Equipment · Projects · Billing
          </p>
          <p className="mt-2 text-center text-[11px] text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
            <Link to="/legal/privacy" data-testid="login-privacy-link" className="hover:text-[#00A3FF] underline-offset-2 hover:underline">Privacy policy</Link>
            <span className="mx-2 text-white/70">·</span>
            <Link to="/legal/terms" data-testid="login-terms-link" className="hover:text-[#FFB020] underline-offset-2 hover:underline">Terms of service</Link>
          </p>
        </div>
      </div>
    </PageShell>
  );
}
